import { useState, useEffect, useCallback, useMemo } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FileText, Eye, Calculator, TrendingUp, Zap, AlertTriangle, Trash2, CheckCircle2, Clock, AlertCircle, Download } from "lucide-react"
import { PropostasService } from "@/services/propostasService"
import { IrradiacaoChart } from "@/components/IrradiacaoChart"
import { GeracaoConsumoChart } from "@/components/GeracaoConsumoChart"
import { irradiacaoService, DadosIrradiacao } from "@/services/irradiacaoService"
import { modulosService } from "@/services/modulosService"
import { supabase } from "@/integrations/supabase/client"
import { fetchWithAuth } from "@/lib/fetchWithAuth"
import { formatBrazilianNumber, formatBrazilianCurrency, formatBrazilianInteger, formatBrazilianPercentage, formatBrazilianCurrencyValue } from "@/utils/formatters"
import { useAuthorization } from "@/contexts/AuthorizationContext"
import { useIsMobile } from "@/hooks/use-mobile"

import type { 
  Proposta, 
  DadosMensais, 
  GeracaoMensal, 
  AnaliseComparativa,
  ParametrosCalculo,
  DadosModuloFotovoltaico,
  ParametrosProjecao,
  ProjecaoCustosEconomia
} from "@/types/propostas"
import { PRODUTO_LEAD_SOLAR_ZERO_GRID } from "@/constants/leadsProduto"

export default function Propostas() {
  const { hasRole, userProfile } = useAuthorization()
  const isParceiro = hasRole('parceiro')
  const isParceiroPai = isParceiro && (userProfile?.pode_criar_rede_parceiros === true)
  const isPropostaFromRede = (p: Proposta | null) =>
    !!p && isParceiroPai && p.lead?.parceiro_id !== userProfile?.id
  /** Parceiro pode criar/gerar proposta dos próprios leads após aprovação do admin; propostas da rede ficam só leitura. */
  const podeCriarProposta = (p: Proposta | null) => !!p && (!isParceiro || !isPropostaFromRede(p))
  const isMobile = useIsMobile()
  
  const [propostas, setPropostas] = useState<Proposta[]>([])
  const [propostasFechadas, setPropostasFechadas] = useState<Proposta[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedProposta, setSelectedProposta] = useState<Proposta | null>(null)
  const [statusPropostaParceiro, setStatusPropostaParceiro] = useState<{
    propostaFinalizada: boolean
    propostaAceita: boolean
    loading: boolean
    pdfUrl: string | null
  } | null>(null)
  const [fatorSimultaneidade, setFatorSimultaneidade] = useState<number>(30) // Percentual (30%)
  const [taxaFioB, setTaxaFioB] = useState<number>(0.07) // Valor em reais (R$ 0,07)
  const [analiseComparativa, setAnaliseComparativa] = useState<AnaliseComparativa[]>([])
  const [dadosMensais, setDadosMensais] = useState<DadosMensais[]>([])
  const [geracaoMensal, setGeracaoMensal] = useState<{ [key: string]: number }>({})
  const [dadosIrradiacao, setDadosIrradiacao] = useState<DadosIrradiacao | null>(null)
  const [loadingIrradiacao, setLoadingIrradiacao] = useState(false)
  const [analiseCarregadaAutomaticamente, setAnaliseCarregadaAutomaticamente] = useState(false)
  const [analiseSalvaComSucesso, setAnaliseSalvaComSucesso] = useState(false)
  const [analiseDeletadaComSucesso, setAnaliseDeletadaComSucesso] = useState(false)
  const [projecaoDeletadaComSucesso, setProjecaoDeletadaComSucesso] = useState(false)
  const [analiseFoiSalva, setAnaliseFoiSalva] = useState(false)
  const [projecaoFoiSalva, setProjecaoFoiSalva] = useState(false)

  // Estados para Projeção de Custos e Economia
  const [modulosKitsSalvos, setModulosKitsSalvos] = useState<DadosModuloFotovoltaico[]>([])
  const [loadingModulosKits, setLoadingModulosKits] = useState(false)
  const [parametrosProjecao, setParametrosProjecao] = useState<ParametrosProjecao>({
    periodoAnalisado: 30,
    inflacaoEnergeticaAnual: 7.0,
    taxaDecaimentoModulos: 0.42
  })
  const [projecaoCalculada, setProjecaoCalculada] = useState<ProjecaoCustosEconomia | null>(null)
  const [loadingProjecao, setLoadingProjecao] = useState(false)
  const [salvandoProjecao, setSalvandoProjecao] = useState(false)
  const [projecaoCarregadaAutomaticamente, setProjecaoCarregadaAutomaticamente] = useState(false)

  // Estados para Geração de Proposta
  const [gerandoProposta, setGerandoProposta] = useState(false)
  const [propostaGerada, setPropostaGerada] = useState<string | null>(null)
  const [validacaoProposta, setValidacaoProposta] = useState<{
    podeGerar: boolean;
    mensagemErro?: string;
    dadosFaltantes: string[];
  } | null>(null)
  const [validandoDados, setValidandoDados] = useState(false)
  const [usarTemplateWord, setUsarTemplateWord] = useState(false)

  // Estados para Deletar Proposta
  const [deletandoProposta, setDeletandoProposta] = useState(false)

  const carregarPropostas = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Carregando propostas...');
      
      const data = await PropostasService.carregarPropostas();
      
      console.log(`✅ ${data.length} propostas carregadas com sucesso`);
      console.log('📋 Propostas carregadas:', data.map(p => ({ id: p.id, proposta_pdf_url: p.proposta_pdf_url })));
      setPropostas(data);
      
      // Carregar propostas fechadas para incluir nas estatísticas
      console.log('🔄 Carregando propostas fechadas para estatísticas...');
      const propostasFechadasData = await PropostasService.carregarPropostasFechadas();
      console.log(`✅ ${propostasFechadasData.length} propostas fechadas carregadas`);
      setPropostasFechadas(propostasFechadasData);
      
      // Se há uma proposta selecionada, atualizar com os dados mais recentes
      if (selectedProposta) {
        const propostaAtualizada = data.find(p => p.id === selectedProposta.id);
        if (propostaAtualizada) {
          console.log('🔄 Atualizando proposta selecionada com dados do servidor:', propostaAtualizada);
          setSelectedProposta(propostaAtualizada);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar propostas:', error);
      
      // Mostrar erro para o usuário
      alert(`Erro ao carregar propostas: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      
      // Manter array vazio em caso de erro
      setPropostas([]);
      setPropostasFechadas([]);
    } finally {
      setLoading(false);
    }
  }, []); // Removido selectedProposta das dependências para evitar loop infinito

  useEffect(() => {
    carregarPropostas()
  }, [carregarPropostas])

  // Efeito para ocultar automaticamente a mensagem de sucesso após 5 segundos
  useEffect(() => {
    if (analiseSalvaComSucesso) {
      const timer = setTimeout(() => {
        setAnaliseSalvaComSucesso(false)
      }, 5000) // 5 segundos

      return () => clearTimeout(timer)
    }
  }, [analiseSalvaComSucesso])

  // Efeito para ocultar automaticamente a mensagem de deleção após 5 segundos
  useEffect(() => {
    if (analiseDeletadaComSucesso) {
      const timer = setTimeout(() => {
        setAnaliseDeletadaComSucesso(false)
      }, 5000) // 5 segundos

      return () => clearTimeout(timer)
    }
  }, [analiseDeletadaComSucesso])

  // Efeito para ocultar automaticamente a mensagem de projeção deletada após 5 segundos
  useEffect(() => {
    if (projecaoDeletadaComSucesso) {
      const timer = setTimeout(() => {
        setProjecaoDeletadaComSucesso(false)
      }, 5000) // 5 segundos

      return () => clearTimeout(timer)
    }
  }, [projecaoDeletadaComSucesso])

  const carregarDadosMensais = async (leadId: string, grupoTarifario: string, dimensionamentoId?: string) => {
    try {
      console.log('Carregando dados mensais para:', { leadId, grupoTarifario, dimensionamentoId });
      
      const { dadosMensais, geracaoMensal } = await PropostasService.carregarDadosMensais(leadId, grupoTarifario, dimensionamentoId);
      
      console.log('Dados mensais carregados:', {
        dadosMensais: dadosMensais.length,
        geracaoMensal: Object.keys(geracaoMensal).length
      });
      
      setDadosMensais(dadosMensais);
      setGeracaoMensal(geracaoMensal as { [key: string]: number });
    } catch (error) {
      console.error('Erro ao carregar dados mensais:', error);
      
      // Mostrar erro para o usuário
      alert(`Erro ao carregar dados mensais: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      
      // Limpar dados em caso de erro
      setDadosMensais([]);
      setGeracaoMensal({});
    }
  }

  const carregarDadosIrradiacao = async (cidade: string, estado: string) => {
    try {
      setLoadingIrradiacao(true);
      console.log('Carregando dados de irradiação para:', { cidade, estado });
      
      const dados = await irradiacaoService.getByCidadeEstado(cidade, estado);
      
      if (dados) {
        console.log('Dados de irradiação carregados:', dados);
        setDadosIrradiacao(dados);
      } else {
        // Tentar busca mais flexível por cidade
        const dadosFlexiveis = await irradiacaoService.getByCidade(cidade);
        if (dadosFlexiveis) {
          console.log('Dados de irradiação encontrados com busca flexível:', dadosFlexiveis);
          setDadosIrradiacao(dadosFlexiveis);
        } else {
          console.log('Nenhum dado de irradiação encontrado para:', { cidade, estado });
          setDadosIrradiacao(null);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados de irradiação:', error);
      setDadosIrradiacao(null);
    } finally {
      setLoadingIrradiacao(false);
    }
  }

  const aplicarParametrosAnaliseLeadZeroGrid = (produtoLead?: string | null) => {
    if (produtoLead === PRODUTO_LEAD_SOLAR_ZERO_GRID) {
      setFatorSimultaneidade(100);
      setTaxaFioB(0);
    }
  };

  const carregarAnaliseComparativaSalva = async (propostaId: string, produtoLead?: string | null) => {
    try {
      console.log('Tentando carregar análise comparativa salva para proposta:', propostaId);
      
      const resultado = await PropostasService.carregarAnaliseComparativaSalva(propostaId);
      
      if (resultado && resultado.analise && resultado.analise.length > 0) {
        console.log('Análise comparativa salva encontrada:', resultado.analise.length, 'meses');
        
        // Carregar a análise salva
        setAnaliseComparativa(resultado.analise);
        setAnaliseCarregadaAutomaticamente(true);
        setAnaliseFoiSalva(true); // Marcar como salva quando carregada do banco
        
        // Carregar parâmetros salvos se disponíveis
        if (resultado.parametros) {
          console.log('Parâmetros salvos encontrados:', resultado.parametros);
          setFatorSimultaneidade(Math.round(resultado.parametros.fatorSimultaneidade * 100)); // Converter para percentual
          setTaxaFioB(resultado.parametros.taxaFioB);
        }
        aplicarParametrosAnaliseLeadZeroGrid(produtoLead);
        
        // Mostrar mensagem para o usuário
        console.log('Análise comparativa carregada automaticamente do banco de dados');
      } else {
        console.log('Nenhuma análise comparativa salva encontrada para esta proposta');
        setAnaliseComparativa([]);
        setAnaliseCarregadaAutomaticamente(false);
      }
    } catch (error) {
      console.error('Erro ao carregar análise comparativa salva:', error);
      // Não mostrar erro para o usuário, apenas limpar a análise
      setAnaliseComparativa([]);
    }
  }

  const carregarProjecaoSalva = async (leadId: string, dimensionamentoId?: string) => {
    try {
      console.log('Tentando carregar projeção salva para:', { leadId, dimensionamentoId });

      const projecaoSalva = await PropostasService.carregarProjecaoSalva(leadId, dimensionamentoId);

      if (projecaoSalva) {
        console.log('Projeção salva encontrada no banco de dados');

        // Carregar a projeção salva
        setProjecaoCalculada(projecaoSalva);
        setProjecaoCarregadaAutomaticamente(true);
        setProjecaoFoiSalva(true); // Marcar como salva quando carregada do banco

        // Carregar parâmetros salvos
        if (projecaoSalva.parametros) {
          console.log('Parâmetros da projeção salvos encontrados:', projecaoSalva.parametros);
          setParametrosProjecao({
            periodoAnalisado: projecaoSalva.parametros.periodoAnalisado,
            inflacaoEnergeticaAnual: projecaoSalva.parametros.inflacaoEnergeticaAnual,
            taxaDecaimentoModulos: projecaoSalva.parametros.taxaDecaimentoModulos
          });
        }

        // Mostrar mensagem para o usuário
        console.log('✅ Projeção carregada automaticamente do banco de dados');
      } else {
        console.log('Nenhuma projeção salva encontrada para este lead');
        setProjecaoCalculada(null);
        setProjecaoCarregadaAutomaticamente(false);
      }
    } catch (error) {
      console.error('Erro ao carregar projeção salva:', error);
      // Não mostrar erro para o usuário, apenas limpar a projeção
      setProjecaoCalculada(null);
      setProjecaoCarregadaAutomaticamente(false);
    }
  }

  const salvarAnaliseComparativa = async () => {
    try {
      if (!selectedProposta?.id) {
        alert('Nenhuma proposta selecionada para salvar a análise');
        return;
      }

      if (!analiseComparativa.length) {
        alert('Nenhuma análise comparativa para salvar');
        return;
      }

      console.log('Salvando análise comparativa para proposta:', selectedProposta.id);

      const zeroGrid = selectedProposta.lead?.produto === PRODUTO_LEAD_SOLAR_ZERO_GRID;
      const parametros: ParametrosCalculo = {
        fatorSimultaneidade: zeroGrid ? 1 : fatorSimultaneidade / 100,
        taxaFioB: zeroGrid ? 0 : taxaFioB
      };

      await PropostasService.salvarAnaliseComparativa(
        selectedProposta.id,
        analiseComparativa,
        parametros
      );

      // Mostrar mensagem de sucesso
      setAnaliseSalvaComSucesso(true);
      setAnaliseFoiSalva(true);

      // Atualizar validação dos dados após salvar
      if (selectedProposta?.id) {
        await validarDadosProposta(selectedProposta.id);
      }

      alert('Análise comparativa salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar análise comparativa:', error);
      alert(`Erro ao salvar análise: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  }

  const deletarAnaliseComparativa = async () => {
    try {
      if (!selectedProposta?.id) {
        alert('Nenhuma proposta selecionada para deletar a análise');
        return;
      }

      // Confirmação de deleção
      const confirmacao = window.confirm(
        `🗑️ DELETAR ANÁLISE COMPARATIVA\n\n` +
        `⚠️ ATENÇÃO: Esta ação não pode ser desfeita!\n\n` +
        `Você está prestes a deletar APENAS a análise comparativa de:\n` +
        `${PropostasService.getNomeCliente(selectedProposta.lead)}\n\n` +
        `Será removido permanentemente:\n` +
        `• A tabela de análise comparativa (SEM SFV vs COM SFV)\n` +
        `• Os parâmetros de cálculo salvos (Fator de Simultaneidade e Taxa Fio B)\n` +
        `• PDF da proposta (se existir) - será necessário gerar novamente\n\n` +
        `Serão MANTIDOS:\n` +
        `• Todos os dados básicos da proposta\n` +
        `• Dados de dimensionamento e potência\n` +
        `• Projeções de custos e economia\n` +
        `• Dados de módulos fotovoltaicos\n` +
        `• Dados mensais de consumo\n` +
        `• Todas as outras etapas anteriores\n\n` +
        `A análise pode ser recalculada a qualquer momento.\n` +
        `O PDF precisará ser gerado novamente após recalcular a análise.\n\n` +
        `Deseja continuar?`
      );

      if (!confirmacao) {
        return;
      }

      console.log('Deletando análise comparativa para proposta:', selectedProposta.id);

      await PropostasService.deletarAnaliseComparativa(selectedProposta.id);

      // Deletar também o PDF da proposta, pois a análise foi alterada
      if (selectedProposta.proposta_pdf_url) {
        console.log('🗑️ Deletando PDF da proposta devido à alteração na análise comparativa');
        await PropostasService.deletarApenasPdfProposta(selectedProposta.id);
      }

      // Limpar dados da análise na interface e mostrar mensagem de sucesso
      setAnaliseComparativa([]);
      setAnaliseCarregadaAutomaticamente(false);
      setAnaliseSalvaComSucesso(false);
      setAnaliseFoiSalva(false);
      setAnaliseDeletadaComSucesso(true);

      // Atualizar a proposta selecionada removendo a URL do PDF
      setSelectedProposta(prev => prev ? { ...prev, proposta_pdf_url: null } : null);

      // Atualizar validação dos dados após deletar
      if (selectedProposta?.id) {
        await validarDadosProposta(selectedProposta.id);
      }

      // Recarregar lista de propostas para atualizar a interface
      await carregarPropostas();

      alert('✅ Análise comparativa deletada com sucesso!\n\nO cliente pode recalcular uma nova análise a qualquer momento.');
    } catch (error) {
      console.error('Erro ao deletar análise comparativa:', error);
      alert(`Erro ao deletar análise: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  }

  const deletarProjecao = async () => {
    try {
      if (!selectedProposta?.dimensionamento_id) {
        alert('Nenhum dimensionamento selecionado para deletar a projeção');
        return;
      }

      // Confirmação de deleção
      const confirmacao = window.confirm(
        `🗑️ DELETAR PROJEÇÃO DE CUSTOS E ECONOMIA\n\n` +
        `⚠️ ATENÇÃO: Esta ação não pode ser desfeita!\n\n` +
        `Você está prestes a deletar APENAS a projeção de custos e economia desta proposta de:\n` +
        `${PropostasService.getNomeCliente(selectedProposta.lead)}\n\n` +
        `Será removido permanentemente:\n` +
        `• Todos os dados da projeção financeira (cenário SEM e COM energia solar)\n` +
        `• Indicadores financeiros (Payback, TIR, Economia Total)\n` +
        `• Parâmetros de cálculo salvos (período analisado, inflação, taxa de decaimento)\n` +
        `• Arrays de projeção por ano (despesas, economia, geração, etc.)\n` +
        `• PDF da proposta (se existir) - será necessário gerar novamente\n\n` +
        `Serão MANTIDOS:\n` +
        `• Todos os dados básicos da proposta\n` +
        `• Dados de dimensionamento e potência\n` +
        `• Análise comparativa\n` +
        `• Dados de módulos fotovoltaicos\n` +
        `• Dados mensais de consumo\n` +
        `• Todas as outras etapas anteriores\n` +
        `• Outras propostas do mesmo cliente (se existirem)\n\n` +
        `A projeção pode ser recalculada a qualquer momento.\n` +
        `O PDF precisará ser gerado novamente após recalcular a projeção.\n\n` +
        `Deseja continuar?`
      );

      if (!confirmacao) {
        return;
      }

      console.log('Deletando projeção de custos e economia para dimensionamento:', selectedProposta.dimensionamento_id);

      await PropostasService.deletarProjecao(selectedProposta.dimensionamento_id);

      // Deletar também o PDF da proposta, pois a projeção foi alterada
      if (selectedProposta.proposta_pdf_url) {
        console.log('🗑️ Deletando PDF da proposta devido à alteração na projeção de custos');
        await PropostasService.deletarApenasPdfProposta(selectedProposta.id);
      }

      // Limpar dados da projeção na interface e mostrar mensagem de sucesso
      setProjecaoCalculada(null);
      setProjecaoCarregadaAutomaticamente(false);
      setProjecaoFoiSalva(false);
      setProjecaoDeletadaComSucesso(true);

      // Atualizar a proposta selecionada removendo a URL do PDF
      setSelectedProposta(prev => prev ? { ...prev, proposta_pdf_url: null } : null);

      // Atualizar validação dos dados após deletar projeção
      if (selectedProposta?.id) {
        await validarDadosProposta(selectedProposta.id);
      }

      // Recarregar lista de propostas para atualizar a interface
      await carregarPropostas();

      alert('✅ Projeção de custos e economia deletada com sucesso!\n\nO cliente pode recalcular uma nova projeção a qualquer momento.');
    } catch (error) {
      console.error('Erro ao deletar projeção:', error);
      alert(`Erro ao deletar projeção: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  }

  const calcularAnaliseComparativa = () => {
    try {
      if (!selectedProposta || !dadosMensais.length) {
        console.warn('Dados insuficientes para cálculo:', { 
          selectedProposta: !!selectedProposta, 
          dadosMensaisLength: dadosMensais.length 
        });
        return;
      }

      const zeroGrid = selectedProposta.lead?.produto === PRODUTO_LEAD_SOLAR_ZERO_GRID;
      const parametros: ParametrosCalculo = {
        fatorSimultaneidade: zeroGrid ? 1 : fatorSimultaneidade / 100,
        taxaFioB: zeroGrid ? 0 : taxaFioB
      }

      console.log('Iniciando cálculo com parâmetros:', parametros);
      console.log('Dados da proposta:', selectedProposta);
      console.log('Dados mensais:', dadosMensais);
      console.log('Geração mensal:', geracaoMensal);

      const analise = PropostasService.calcularAnaliseComparativa(
        selectedProposta,
        dadosMensais,
        geracaoMensal,
        parametros
      )

      console.log('Cálculo concluído com sucesso:', analise);
      setAnaliseComparativa(analise);
      setAnaliseFoiSalva(false); // Resetar estado quando nova análise for calculada
    } catch (error) {
      console.error('Erro durante o cálculo da análise comparativa:', error);
      
      // Mostrar erro para o usuário de forma amigável
      // Em vez de deixar o Sentry capturar e causar problemas
      alert(`Erro durante o cálculo: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      
      // Limpar análise anterior em caso de erro
      setAnaliseComparativa([]);
    }
  }

  const buscarStatusPropostaParceiro = async (propostaId: string) => {
    try {
      setStatusPropostaParceiro({ propostaFinalizada: false, propostaAceita: false, loading: true, pdfUrl: null });
      
      const { data, error } = await supabase
        .from('propostas_clientes')
        .select('proposta_pdf_url, proposta_aceita')
        .eq('id', propostaId)
        .maybeSingle();

      if (error) throw error;

      const propostaFinalizada = data?.proposta_pdf_url !== null && data?.proposta_pdf_url !== undefined;
      const propostaAceita = data?.proposta_aceita === true;
      const pdfUrl = data?.proposta_pdf_url ?? null;

      setStatusPropostaParceiro({
        propostaFinalizada,
        propostaAceita,
        loading: false,
        pdfUrl
      });
    } catch (error) {
      console.error('Erro ao buscar status da proposta:', error);
      setStatusPropostaParceiro({ propostaFinalizada: false, propostaAceita: false, loading: false, pdfUrl: null });
    }
  };

  const handlePropostaChange = async (propostaId: string) => {
    try {
      const proposta = propostas.find(p => p.id === propostaId);
      if (!proposta) {
        throw new Error('Proposta não encontrada');
      }

      console.log('Proposta selecionada:', proposta);
      console.log('Dados do lead:', proposta.lead);
      console.log('Cidade:', proposta.lead?.cidade);
      console.log('Estado:', proposta.lead?.estado);
      
      if (!proposta.lead_id) {
        throw new Error('ID do lead não encontrado na proposta');
      }
      
      // Parceiro pai em proposta da rede: apenas status (somente leitura)
      if (isParceiro && isPropostaFromRede(proposta)) {
        setSelectedProposta(proposta);
        await buscarStatusPropostaParceiro(propostaId);
        return;
      }
      
      if (!proposta.lead?.grupo_tarifario) {
        throw new Error('Grupo tarifário não encontrado no lead');
      }
      
      // CORREÇÃO: Limpar TODOS os estados relacionados antes de carregar nova proposta
      setSelectedProposta(null); // Limpar primeiro
      setStatusPropostaParceiro(null);
      setAnaliseCarregadaAutomaticamente(false);
      setAnaliseSalvaComSucesso(false);
      setAnaliseDeletadaComSucesso(false);
      setAnaliseFoiSalva(false);
      setProjecaoFoiSalva(false);
      setProjecaoDeletadaComSucesso(false);
      setProjecaoCarregadaAutomaticamente(false);
      
      // Limpar dados de projeção e análise para evitar valores incorretos
      setProjecaoCalculada(null);
      setAnaliseComparativa([]);
      setDadosMensais([]);
      setGeracaoMensal({});
      setModulosKitsSalvos([]);
      
      // Aguardar um tick para garantir que os estados sejam limpos
      await new Promise(resolve => setTimeout(resolve, 0));
      
      // Agora definir a nova proposta
      setSelectedProposta(proposta);

      if (proposta.lead?.produto === PRODUTO_LEAD_SOLAR_ZERO_GRID) {
        setFatorSimultaneidade(100);
        setTaxaFioB(0);
      } else {
        setFatorSimultaneidade(30);
        setTaxaFioB(0.07);
      }
      
      console.log('Carregando dados mensais para:', {
        leadId: proposta.lead_id,
        grupoTarifario: proposta.lead?.grupo_tarifario,
        dimensionamentoId: proposta.dimensionamento_id
      });
      
      if (!proposta.lead?.grupo_tarifario) {
        throw new Error('Grupo tarifário não encontrado no lead');
      }
      
      await carregarDadosMensais(proposta.lead_id, proposta.lead.grupo_tarifario, proposta.dimensionamento_id);
      
      // Carregar dados de irradiação se cidade e estado estiverem disponíveis
      if (proposta.lead?.cidade && proposta.lead?.estado) {
        await carregarDadosIrradiacao(proposta.lead.cidade, proposta.lead.estado);
      }
      
      // Tentar carregar análise comparativa salva
      await carregarAnaliseComparativaSalva(proposta.id, proposta.lead?.produto);

      // Tentar carregar projeção salva com logs detalhados
      console.log('🔄 Carregando projeção salva para:', {
        leadId: proposta.lead_id,
        dimensionamentoId: proposta.dimensionamento_id,
        propostaId: proposta.id
      });
      await carregarProjecaoSalva(proposta.lead_id, proposta.dimensionamento_id);

      // Validar se todos os dados necessários para gerar proposta estão disponíveis
      await validarDadosProposta(proposta.id);

      console.log('Dados mensais carregados com sucesso');
    } catch (error) {
      console.error('Erro ao selecionar proposta:', error);
      
      // Mostrar erro para o usuário
      alert(`Erro ao carregar dados da proposta: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      
      // Limpar dados em caso de erro
      setSelectedProposta(null);
      setDadosMensais([]);
      setGeracaoMensal({});
      setAnaliseComparativa([]);
      setStatusPropostaParceiro(null);
    }
  }

  // Combinar propostas abertas e fechadas para calcular estatísticas
  const todasPropostas = useMemo(() => {
    // Criar um Set para evitar duplicatas (usando ID da proposta)
    const propostasUnicas = new Map<string, Proposta>();
    
    // Adicionar propostas abertas
    propostas.forEach(p => propostasUnicas.set(p.id, p));
    
    // Adicionar propostas fechadas (sobrescrevendo se já existir, mas não deve acontecer)
    propostasFechadas.forEach(p => {
      if (!propostasUnicas.has(p.id)) {
        propostasUnicas.set(p.id, p);
      }
    });
    
    return Array.from(propostasUnicas.values());
  }, [propostas, propostasFechadas]);

  const estatisticas = PropostasService.calcularEstatisticas(todasPropostas)

  // Função para deletar apenas o PDF da proposta
  const deletarProposta = async () => {
    if (!selectedProposta?.id) {
      alert('Nenhuma proposta selecionada para deletar');
      return;
    }

    // Verificar se existe um PDF para deletar
    if (!selectedProposta.proposta_pdf_url) {
      alert('Esta proposta não possui um PDF para deletar.');
      return;
    }

    // Confirmação de deleção
    const confirmacao = window.confirm(
      `🆕 VERSÃO ATUALIZADA - TESTE DE CACHE\n\n` +
      `⚠️ ATENÇÃO: Esta ação não pode ser desfeita!\n\n` +
      `Você está prestes a deletar APENAS o PDF da proposta de:\n` +
      `${PropostasService.getNomeCliente(selectedProposta.lead)}\n\n` +
      `Será removido permanentemente:\n` +
      `• Apenas o documento PDF da proposta\n\n` +
      `Serão MANTIDOS:\n` +
      `• Todos os dados da proposta\n` +
      `• Projeções de custos e economia\n` +
      `• Dados de análise comparativa\n` +
      `• Dados de dimensionamento\n` +
      `• Todas as etapas anteriores\n\n` +
      `O cliente poderá gerar uma nova proposta a qualquer momento.\n\n` +
      `Deseja continuar?`
    );

    if (!confirmacao) {
      return;
    }

    try {
      setDeletandoProposta(true);
      console.log('🔍 INICIANDO: Removendo apenas o PDF da proposta:', selectedProposta.id);
      console.log('🔍 Chamando PropostasService.deletarApenasPdfProposta() - NÃO deve deletar dados de dimensionamento');

      await PropostasService.deletarApenasPdfProposta(selectedProposta.id);

      // Atualizar a proposta selecionada removendo a URL do PDF
      setSelectedProposta(prev => prev ? { ...prev, proposta_pdf_url: null } : null);

      // Recarregar lista de propostas para atualizar a interface
      await carregarPropostas();

      alert('✅ PDF da proposta removido com sucesso!\n\nO cliente pode gerar uma nova proposta a qualquer momento.');

    } catch (error) {
      console.error('Erro ao remover PDF da proposta:', error);
      alert(`Erro ao remover PDF da proposta: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setDeletandoProposta(false);
    }
  };

  // Funções para Projeção de Custos e Economia
  const carregarModulosFotovoltaicos = useCallback(async () => {
    try {
      // Verificar se há uma proposta selecionada
      if (!selectedProposta?.id) {
        console.log('Nenhuma proposta selecionada, não carregando módulos');
        setModulosKitsSalvos([]);
        return;
      }

      console.log('Iniciando carregamento de módulos fotovoltaicos para proposta:', selectedProposta.id);
      setLoadingModulosKits(true);
      
      const modulos = await PropostasService.buscarModulosKitsSalvos(selectedProposta.id);
      console.log('Módulos retornados pelo serviço:', modulos);
      
      // Mapear apenas os campos necessários
      const modulosSimplificados: DadosModuloFotovoltaico[] = modulos.map(modulo => ({
        marca: modulo.marca,
        modelo: modulo.modelo,
        potencia: modulo.potencia,
        tecnologia: modulo.tecnologia,
        encontrado: true // Adicionar um campo para indicar se o módulo foi encontrado
      }));
      
      console.log(`${modulosSimplificados.length} módulos carregados com sucesso:`, modulosSimplificados);
      setModulosKitsSalvos(modulosSimplificados);
    } catch (error) {
      console.error('Erro ao carregar módulos fotovoltaicos:', error);
      alert(`Erro ao carregar módulos: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      setModulosKitsSalvos([]);
    } finally {
      setLoadingModulosKits(false);
    }
  }, [selectedProposta?.id])

  const calcularProjecao = async () => {
    try {
      if (!selectedProposta?.lead_id) {
        alert('Nenhuma proposta selecionada para calcular a projeção');
        return;
      }

      setLoadingProjecao(true);
      console.log('🧮 Calculando projeção de custos e economia...');
      console.log('📋 Dados da proposta selecionada:', {
        propostaId: selectedProposta.id,
        leadId: selectedProposta.lead_id,
        dimensionamentoId: selectedProposta.dimensionamento_id,
        parametros: parametrosProjecao
      });
      
      // CORREÇÃO: Limpar projeção anterior antes de calcular nova
      setProjecaoCalculada(null);
      
      // Usar a nova API Python para calcular a projeção
      const projecao = await PropostasService.calcularProjecaoCustosEconomiaAPI(
        selectedProposta.lead_id,
        parametrosProjecao,
        selectedProposta.dimensionamento_id
      );
      
      console.log('✅ Projeção calculada com sucesso:', projecao);
      setProjecaoCalculada(projecao);
      setProjecaoFoiSalva(false); // Resetar estado quando nova projeção for calculada
    } catch (error) {
      console.error('Erro ao calcular projeção:', error);
      
      // Se a API falhar, não mostrar dados incorretos
      const mensagemErro = error instanceof Error ? error.message : 'Erro desconhecido';
      
      if (mensagemErro.includes('Não foi possível calcular a projeção')) {
        alert(`⚠️ ATENÇÃO: ${mensagemErro}\n\nA projeção da "Situação COM Energia Solar" não pode ser calculada no momento. Por favor, tente novamente mais tarde ou entre em contato com o suporte.`);
      } else {
        alert(`Erro ao calcular projeção: ${mensagemErro}`);
      }
      
      // Limpar dados de projeção para não mostrar informações incorretas
      setProjecaoCalculada(null);
    } finally {
      setLoadingProjecao(false);
    }
  };

  const salvarProjecao = async () => {
    try {
      if (!selectedProposta?.lead_id || !projecaoCalculada) {
        alert('Nenhuma projeção calculada para salvar');
        return;
      }

      setSalvandoProjecao(true);
      console.log('Salvando projeção no Supabase...');

      // Buscar dados da projeção da API Python para obter todos os dados detalhados
      const response = await fetch('/api/projecao-grupob', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lead_id: selectedProposta.lead_id,
          dimensionamento_id: selectedProposta.dimensionamento_id,
          parametros_frontend: parametrosProjecao
        })
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      const dadosCompletos = await response.json();
      console.log('Dados completos da projeção:', dadosCompletos);
      console.log('Indicadores financeiros:', dadosCompletos.indicadores_financeiros);

      // Preparar dados para inserção no Supabase
      const dadosProjecao = {
        lead_id: selectedProposta.lead_id,
        proposta_id: selectedProposta.id,
        dimensionamento_id: selectedProposta.dimensionamento_id,
        periodo_analisado: parametrosProjecao.periodoAnalisado,
        inflacao_energetica_anual: parametrosProjecao.inflacaoEnergeticaAnual,
        taxa_decaimento_modulos: parametrosProjecao.taxaDecaimentoModulos,

        // Dados do cliente necessários para a situação sem energia solar
        consumo_anual: dadosCompletos.cliente.consumo_anual,
        tarifa_atual: dadosCompletos.cliente.tarifa_atual,
        cosip: dadosCompletos.cliente.cosip,
        geracao_ano1: dadosCompletos.cliente.geracao_ano1,
        
        // Dados da projeção (arrays JSONB)
        ano: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.ano),
        tarifa: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.tarifa),
        despesa: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.despesa),
        despesa_acumulada: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.despesa_acumulada),
        consumo_acumulado: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.consumo_acumulado),
        geracao_sistema: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.geracao_sistema),
        geracao_inst_consumida: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.geracao_inst_consumida),
        geracao_injetada: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.geracao_injetada),
        geracao_acumulada: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.geracao_acumulada),
        consumo_faturado: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.consumo_faturado),
        creditos_consumidos: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.creditos_consumidos),
        credito_energetico: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.credito_energetico),
        credito_acumulado: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.credito_acumulado),
        consumo_remanescente: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.consumo_remanescente),
        despesa_remanescente: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.despesa_remanescente),
        manutencao: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.manutencao),
        economia: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.economia),
        economia_acumulada: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.economia_acumulada),
        financiamento: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.financiamento),
        resultado_fluxo_caixa: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.resultado_fluxo_caixa),
        resultado_acumulado: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.resultado_acumulado),
        fluxo_descontado: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.fluxo_descontado),
        resultado_descontado: dadosCompletos.projecao_anos.map((ano: Record<string, unknown>) => ano.resultado_descontado),
        
        // Indicadores financeiros
        payback_simples: dadosCompletos.indicadores_financeiros.payback_simples,
        payback_descontado: dadosCompletos.indicadores_financeiros.payback_descontado,
        roe_simples: dadosCompletos.indicadores_financeiros.roe_simples,
        roe_anualizado: dadosCompletos.indicadores_financeiros.roe_anualizado,
        roe_descontado: dadosCompletos.indicadores_financeiros.roe_descontado,
        roe_medio_anual: dadosCompletos.indicadores_financeiros.roe_medio_anual,
        economia_total: dadosCompletos.indicadores_financeiros.economia_total,
        investimento: dadosCompletos.indicadores_financeiros.investimento,
        resultado_descontado_final: dadosCompletos.indicadores_financeiros.resultado_descontado_final,
        vida_util: dadosCompletos.indicadores_financeiros.vida_util
      };

      console.log('Dados preparados para Supabase:', dadosProjecao);
      console.log('Campos ROE sendo salvos:', {
        roe_simples: dadosProjecao.roe_simples,
        roe_anualizado: dadosProjecao.roe_anualizado,
        roe_descontado: dadosProjecao.roe_descontado,
        roe_medio_anual: dadosProjecao.roe_medio_anual
      });

      // Verificar se já existe uma projeção para este dimensionamento específico
      console.log('Verificando se já existe projeção para:', {
        lead_id: selectedProposta.lead_id,
        dimensionamento_id: selectedProposta.dimensionamento_id
      });

      // Primeiro tentar encontrar uma projeção existente usando dimensionamento_id
      const { data: projecoesExistentes, error: erroVerificacao } = await supabase
        .from('projecao_grupob')
        .select('id, created_at')
        .eq('dimensionamento_id', selectedProposta.dimensionamento_id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (erroVerificacao) {
        console.error('Erro na verificação:', erroVerificacao);
        throw erroVerificacao;
      }

      let data, error;

      if (projecoesExistentes && projecoesExistentes.length > 0) {
        // Já existe uma projeção, fazer UPDATE da mais recente
        const projecaoMaisRecente = projecoesExistentes[0];
        console.log('Projeção existente encontrada, fazendo UPDATE:', projecaoMaisRecente.id);

        ({ data, error } = await supabase
          .from('projecao_grupob')
          .update(dadosProjecao)
          .eq('id', projecaoMaisRecente.id)
          .select());

        if (error) {
          console.error('Erro no UPDATE:', error);
          throw error;
        }

        console.log('Projeção atualizada com sucesso:', data);
        setProjecaoFoiSalva(true);
        alert('✅ Projeção atualizada com sucesso no banco de dados!');
      } else {
        // Não existe projeção, fazer INSERT
        console.log('Nenhuma projeção existente encontrada, fazendo INSERT');
        ({ data, error } = await supabase
          .from('projecao_grupob')
          .insert([dadosProjecao])
          .select());

        if (error) {
          console.error('Erro no INSERT:', error);
          throw error;
        }

        console.log('Projeção salva com sucesso:', data);
        setProjecaoFoiSalva(true);

        // Atualizar validação dos dados após salvar projeção
        if (selectedProposta?.id) {
          await validarDadosProposta(selectedProposta.id);
        }

        alert('✅ Projeção salva com sucesso no banco de dados!');
      }
      
    } catch (error) {
      console.error('Erro ao salvar projeção:', error);
      alert(`Erro ao salvar projeção: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setSalvandoProjecao(false);
    }
  };

  const validarDadosProposta = async (propostaId: string) => {
    try {
      setValidandoDados(true);
      console.log('Validando dados para geração da proposta:', propostaId);

      const resultadoValidacao = await PropostasService.validarDadosParaGeracao(propostaId);

      console.log('Resultado da validação:', resultadoValidacao);
      setValidacaoProposta(resultadoValidacao);

      if (!resultadoValidacao.podeGerar) {
        console.log('Dados insuficientes para gerar proposta:', resultadoValidacao.dadosFaltantes);
      }

    } catch (error) {
      console.error('Erro ao validar dados da proposta:', error);
      setValidacaoProposta({
        podeGerar: false,
        mensagemErro: 'Erro ao validar dados. Tente novamente.',
        dadosFaltantes: ['Erro na validação']
      });
    } finally {
      setValidandoDados(false);
    }
  };

  const gerarProposta = async () => {
    try {
      if (!selectedProposta?.id) {
        alert('Nenhuma proposta selecionada para gerar');
        return;
      }

      // Usar validação RIGOROSA apenas quando o usuário clica em "Gerar"
      console.log('🔒 Executando validação rigorosa antes de gerar proposta...');
      const validacaoEstrita = await PropostasService.validarDadosParaGeracaoEstrita(selectedProposta.id);

      if (!validacaoEstrita.podeGerar) {
        alert(validacaoEstrita.mensagemErro || 'Dados insuficientes para gerar a proposta');
        return;
      }

      setGerandoProposta(true);
      console.log('Gerando proposta PDF para:', selectedProposta.id);

      const response = await fetchWithAuth('/api/propostas/gerar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propostaId: selectedProposta.id,
          usarTemplateWord: usarTemplateWord
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erro na API: ${response.status}`);
      }

      const result = await response.json();
      console.log('Proposta gerada com sucesso:', result);

      setPropostaGerada(result.pdfUrl);

      // Atualizar o estado da proposta selecionada com a nova URL do PDF
      setSelectedProposta(prev => {
        const updated = prev ? { ...prev, proposta_pdf_url: result.pdfUrl } : null;
        console.log('🔄 Atualizando proposta selecionada:', updated);
        return updated;
      });

      // Recarregar lista de propostas para atualizar a interface
      console.log('🔄 Recarregando lista de propostas...');
      await carregarPropostas();

      // Mostrar sucesso e opções
      const download = confirm('Proposta gerada com sucesso! Deseja fazer o download?');
      if (download) {
        window.open(result.pdfUrl, '_blank');
      }

    } catch (error) {
      console.error('Erro ao gerar proposta:', error);
      alert(`Erro ao gerar proposta: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setGerandoProposta(false);
    }
  };

  // Carregar módulos quando uma proposta for selecionada
  useEffect(() => {
    if (selectedProposta?.id) {
      carregarModulosFotovoltaicos();
    }
  }, [selectedProposta?.id, carregarModulosFotovoltaicos])

  return (
    <div className={`space-y-6 ${isMobile ? 'p-4' : 'p-6'} overflow-x-hidden max-w-full w-full`}>
      <div className="flex items-center justify-between w-full">
        <div className="w-full min-w-0">
          <h1 className="text-3xl font-bold text-foreground">Propostas FV</h1>
          <p className="text-muted-foreground">Cotações e contratos solares</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
        <Card className="w-full min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Propostas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{estatisticas.totalPropostas}</div>
          </CardContent>
        </Card>
        <Card className="w-full min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Potência Total Painéis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {estatisticas.potenciaPaineis} kWp
            </div>
          </CardContent>
        </Card>
        <Card className="w-full min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Potência Total Inversores</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {estatisticas.potenciaTotal} kW
            </div>
          </CardContent>
        </Card>
        <Card className="w-full min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Média por Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {estatisticas.mediaPorSistema} kW
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Seleção de Proposta */}
      <Card className="w-full overflow-x-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Selecionar Proposta
          </CardTitle>
          <CardDescription>
            {isParceiro
              ? 'Escolha um cliente com dimensionamento aprovado pelo admin para criar a proposta'
              : 'Escolha um cliente com dimensionamento finalizado para análise'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Carregando...</div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="proposta-select">Cliente</Label>
                <Select onValueChange={handlePropostaChange} value={selectedProposta?.id || ""}>
                  <SelectTrigger id="proposta-select" className="w-full">
                    <SelectValue placeholder="Selecione um cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {propostas.map((proposta) => (
                      <SelectItem key={proposta.id} value={proposta.id}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">
                            {PropostasService.getNomeCliente(proposta.lead)}
                            {proposta.dimensionamento && (
                              <span className="text-blue-600 ml-2">
                                - {proposta.dimensionamento.nome_dimensionamento}
                              </span>
                            )}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {proposta.lead?.grupo_tarifario || 'N/A'} • {proposta.lead?.padrao || 'N/A'} • {proposta.potencia_total_inversores} kW
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Informações da Proposta Selecionada */}
              {selectedProposta && podeCriarProposta(selectedProposta) && (
                <div className="p-4 border border-border rounded-lg bg-muted/30 w-full">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 w-full">
                    <div className="w-full min-w-0">
                      <Label className="text-sm font-medium">Cliente</Label>
                      <p className="text-sm break-words">
                        {PropostasService.getNomeCliente(selectedProposta.lead)}
                        {selectedProposta.dimensionamento && (
                          <span className="text-blue-600 ml-2">
                            - {selectedProposta.dimensionamento.nome_dimensionamento}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="w-full min-w-0">
                      <Label className="text-sm font-medium">Localidade</Label>
                      <p className="text-sm break-words">
                        {selectedProposta.lead.cidade && selectedProposta.lead.estado
                          ? `${selectedProposta.lead.cidade}/${selectedProposta.lead.estado}`
                          : 'Não informado'
                        }
                      </p>
                    </div>
                    <div className="w-full min-w-0">
                      <Label className="text-sm font-medium">Grupo Tarifário</Label>
                      <p className="text-sm break-words">{selectedProposta.lead?.grupo_tarifario || 'N/A'} • {selectedProposta.lead?.padrao || 'N/A'}</p>
                    </div>
                    <div className="w-full min-w-0">
                      <Label className="text-sm font-medium">Sistema</Label>
                      <p className="text-sm break-words">{selectedProposta.potencia_total_inversores} kW / {selectedProposta.potencia_total_paineis} kWp</p>
                    </div>
                  </div>


                </div>
              )}


            </div>
          )}
        </CardContent>
      </Card>

      {/* Status somente leitura: propostas da rede do parceiro pai */}
      {selectedProposta && isParceiro && isPropostaFromRede(selectedProposta) && (
        <Card className="border-2 w-full overflow-x-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Status da Proposta
            </CardTitle>
            <CardDescription>
              Cliente: {PropostasService.getNomeCliente(selectedProposta.lead)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {statusPropostaParceiro?.loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-muted-foreground">Carregando status...</span>
              </div>
            ) : statusPropostaParceiro?.propostaAceita ? (
              <div className="flex items-start gap-4 p-6 bg-green-50 border-2 border-green-200 rounded-lg">
                <CheckCircle2 className="h-8 w-8 text-green-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">
                    Proposta Finalizada e Aceita pelo Cliente
                  </h3>
                  <p className="text-green-700">
                    A proposta foi finalizada e aceita pelo cliente. O processo de contratação pode prosseguir.
                  </p>
                </div>
              </div>
            ) : statusPropostaParceiro?.propostaFinalizada ? (
              <div className="flex items-start gap-4 p-6 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                <Clock className="h-8 w-8 text-yellow-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                    Proposta Finalizada e Aguarda Aprovação do Cliente
                  </h3>
                  <p className="text-yellow-700">
                    A proposta foi finalizada e está aguardando aprovação por parte do cliente.
                  </p>
                  {(statusPropostaParceiro.pdfUrl || selectedProposta?.proposta_pdf_url) && (
                    <div className="flex flex-wrap gap-3 mt-4">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-yellow-600 text-yellow-800 hover:bg-yellow-100"
                        onClick={() => {
                          const url = statusPropostaParceiro?.pdfUrl || selectedProposta?.proposta_pdf_url;
                          if (url) window.open(url, '_blank');
                        }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Visualizar proposta
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-yellow-600 text-yellow-800 hover:bg-yellow-100"
                        onClick={() => {
                          const url = statusPropostaParceiro?.pdfUrl || selectedProposta?.proposta_pdf_url;
                          if (url) window.open(url, '_blank');
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Baixar proposta
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <AlertCircle className="h-8 w-8 text-blue-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">
                    Proposta Pendente
                  </h3>
                  <p className="text-blue-700">
                    A proposta ainda está em processo de finalização. Aguarde a conclusão para visualizar o status.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Gráficos - Irradiação e Geração x Consumo */}
      {selectedProposta && podeCriarProposta(selectedProposta) && (
        <div className={`${isMobile ? 'flex flex-col' : 'grid grid-cols-1 lg:grid-cols-2'} gap-6 w-full`}>
          {/* Container do gráfico de Irradiação com scroll horizontal no mobile */}
          <div className={`${isMobile ? 'w-full overflow-x-auto overflow-y-hidden' : 'w-full'}`} style={isMobile ? { WebkitOverflowScrolling: 'touch' } : {}}>
            <div className={isMobile ? 'min-w-[600px]' : 'w-full'}>
              <IrradiacaoChart 
                dados={dadosIrradiacao} 
                loading={loadingIrradiacao} 
              />
            </div>
          </div>
          {/* Container do gráfico de Geração x Consumo com scroll horizontal no mobile */}
          <div className={`${isMobile ? 'w-full overflow-x-auto overflow-y-hidden' : 'w-full'}`} style={isMobile ? { WebkitOverflowScrolling: 'touch' } : {}}>
            <div className={isMobile ? 'min-w-[600px]' : 'w-full'}>
              <GeracaoConsumoChart 
                dadosMensais={dadosMensais}
                geracaoMensal={geracaoMensal}
                potenciaTotalPaineis={selectedProposta.potencia_total_paineis || 0}
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}



      {/* Análise Comparativa */}
      {selectedProposta && podeCriarProposta(selectedProposta) && (
        <Card className="w-full overflow-x-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Análise Comparativa
            </CardTitle>
            <CardDescription>
              Cliente: {PropostasService.getNomeCliente(selectedProposta.lead)}
            </CardDescription>
            {analiseCarregadaAutomaticamente && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  ✅ Análise comparativa carregada automaticamente do banco de dados
                </p>
              </div>
            )}

            {analiseSalvaComSucesso && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">
                  ✅ Análise comparativa salva com sucesso!
                </p>
              </div>
            )}

            {analiseDeletadaComSucesso && (
              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
                <p className="text-sm text-orange-800">
                  🗑️ Análise comparativa deletada com sucesso!
                </p>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Parâmetros de Cálculo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              {selectedProposta.lead?.produto === PRODUTO_LEAD_SOLAR_ZERO_GRID && (
                <div className="sm:col-span-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                  Lead <strong>Sistema Solar Zero-Grid</strong>: fator de simultaneidade fixo em 100% e taxa do Fio B em R$ 0,00.
                </div>
              )}
              <div className="w-full min-w-0">
                <Label htmlFor="fatorSimultaneidade">Fator de Simultaneidade (%)</Label>
                <Input
                  id="fatorSimultaneidade"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={fatorSimultaneidade}
                  onChange={(e) => setFatorSimultaneidade(parseInt(e.target.value) || 0)}
                  placeholder="30"
                  className="w-full"
                  disabled={selectedProposta.lead?.produto === PRODUTO_LEAD_SOLAR_ZERO_GRID}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Ex: 30 = 30%
                </p>
              </div>
              <div className="w-full min-w-0">
                <Label htmlFor="taxaFioB">Taxa do Fio B (R$)</Label>
                <Input
                  id="taxaFioB"
                  type="number"
                  step="0.01"
                  min="0"
                  value={taxaFioB}
                  onChange={(e) => setTaxaFioB(parseFloat(e.target.value) || 0)}
                  placeholder="0.07"
                  className="w-full"
                  disabled={selectedProposta.lead?.produto === PRODUTO_LEAD_SOLAR_ZERO_GRID}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Ex: 0.07 = R$ 0,07
                </p>
              </div>
            </div>

            <Button 
              onClick={calcularAnaliseComparativa}
              className="w-full"
              disabled={!dadosMensais.length}
            >
              <Calculator className="h-4 w-4 mr-2" />
              Calcular
            </Button>

            {/* Tabela de Análise */}
            {analiseComparativa.length > 0 && (
              <div className="space-y-4">
                <div className="relative w-full">
                  <div
                    className={`${isMobile ? 'overflow-x-auto overflow-y-hidden w-full' : 'overflow-x-visible max-w-full'} sm:mx-0 sm:px-0`}
                    style={isMobile ? { WebkitOverflowScrolling: 'touch' } : {}}
                  >
                    <div className="min-w-[800px] inline-block w-full">
                      <div className="mb-2 text-xs text-muted-foreground sm:hidden">
                        ← Deslize horizontalmente para ver todas as colunas →
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Mês</TableHead>
                            <TableHead>Consumo</TableHead>
                            <TableHead className="text-sun7-blue font-semibold">SEM SFV</TableHead>
                            <TableHead>Geração</TableHead>
                            <TableHead>Inst. Consumido</TableHead>
                            <TableHead>Injetado Rede</TableHead>
                            <TableHead>Consumo Faturado</TableHead>
                            <TableHead>Balanço Fatura</TableHead>
                            <TableHead>Créditos Consumidos</TableHead>
                            <TableHead>Créditos Acumulados</TableHead>
                            <TableHead className="text-sun7-blue font-semibold">COM SFV</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {analiseComparativa.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{row.mes}</TableCell>
                              <TableCell>{formatBrazilianNumber(row.consumo, 2)}</TableCell>
                              <TableCell className="text-sun7-blue font-medium">{formatBrazilianCurrency(row.semSfv)}</TableCell>
                              <TableCell>{formatBrazilianNumber(row.geracao, 2)}</TableCell>
                              <TableCell>{formatBrazilianNumber(row.instConsumido, 2)}</TableCell>
                              <TableCell>{formatBrazilianNumber(row.injetadoRede, 2)}</TableCell>
                              <TableCell>{formatBrazilianNumber(row.consumoFaturado, 2)}</TableCell>
                              <TableCell>{formatBrazilianNumber(row.balancoFatura, 2)}</TableCell>
                              <TableCell>{formatBrazilianNumber(row.creditosConsumidos, 2)}</TableCell>
                              <TableCell>{formatBrazilianNumber(row.creditosAcumulados, 2)}</TableCell>
                              <TableCell className="text-sun7-blue font-medium">{formatBrazilianCurrency(row.comSfv)}</TableCell>
                            </TableRow>
                          ))}
                          {/* Linha de SOMA */}
                          <TableRow className="font-bold bg-muted/50">
                            <TableCell>SOMA</TableCell>
                            <TableCell>
                              {formatBrazilianNumber(analiseComparativa.reduce((acc, row) => acc + row.consumo, 0), 2)}
                            </TableCell>
                            <TableCell className="text-sun7-blue">
                              {formatBrazilianCurrency(analiseComparativa.reduce((acc, row) => acc + row.semSfv, 0))}
                            </TableCell>
                            <TableCell>
                              {formatBrazilianNumber(analiseComparativa.reduce((acc, row) => acc + row.geracao, 0), 2)}
                            </TableCell>
                            <TableCell>
                              {formatBrazilianNumber(analiseComparativa.reduce((acc, row) => acc + row.instConsumido, 0), 2)}
                            </TableCell>
                            <TableCell>
                              {formatBrazilianNumber(analiseComparativa.reduce((acc, row) => acc + row.injetadoRede, 0), 2)}
                            </TableCell>
                            <TableCell>
                              {formatBrazilianNumber(analiseComparativa.reduce((acc, row) => acc + row.consumoFaturado, 0), 2)}
                            </TableCell>
                            <TableCell>
                              {formatBrazilianNumber(analiseComparativa.reduce((acc, row) => acc + row.balancoFatura, 0), 2)}
                            </TableCell>
                            <TableCell>
                              {formatBrazilianNumber(analiseComparativa.reduce((acc, row) => acc + row.creditosConsumidos, 0), 2)}
                            </TableCell>
                            <TableCell>
                              {formatBrazilianNumber(analiseComparativa.reduce((acc, row) => acc + row.creditosAcumulados, 0), 2)}
                            </TableCell>
                            <TableCell className="text-sun7-blue">
                              {formatBrazilianCurrency(analiseComparativa.reduce((acc, row) => acc + row.comSfv, 0))}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
                
                {/* Botões de Ação da Análise */}
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} justify-center gap-4`}>
                  <Button
                    onClick={salvarAnaliseComparativa}
                    className={`bg-green-600 hover:bg-green-700 text-white ${isMobile ? 'w-full px-4 py-2' : 'px-6 py-2'}`}
                    disabled={!selectedProposta?.id || analiseFoiSalva}
                    title={analiseFoiSalva ? "Análise já foi salva. Calcule uma nova análise para poder salvar novamente." : ""}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Salvar Análise
                  </Button>

                  <Button
                    onClick={deletarAnaliseComparativa}
                    variant="destructive"
                    className={isMobile ? 'w-full px-4 py-2' : 'px-6 py-2'}
                    disabled={!selectedProposta?.id}
                    title="Apagar apenas a análise comparativa desta proposta, mantendo todos os outros dados intactos"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Deletar Análise
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Projeção de Custos e Economia */}
      {selectedProposta && podeCriarProposta(selectedProposta) && (
        <Card className="w-full overflow-x-hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Projeção de Custos e Economia
            </CardTitle>
            <CardDescription>
              Cliente: {PropostasService.getNomeCliente(selectedProposta.lead)}
            </CardDescription>
            {projecaoCalculada && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-green-600 font-medium">
                      Projeção calculada com sucesso
                    </span>
                  </div>
                </div>
                {projecaoCarregadaAutomaticamente && (
                  <p className="text-sm text-green-800 mt-1">
                    ✅ Projeção carregada automaticamente do banco de dados
                  </p>
                )}
                {projecaoDeletadaComSucesso && (
                  <p className="text-sm text-orange-800 mt-1">
                    🗑️ Projeção de custos e economia deletada com sucesso!
                  </p>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Informações dos Módulos Fotovoltaicos */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Módulos Fotovoltaicos Disponíveis
              </h3>
              {loadingModulosKits ? (
                <div className="text-center py-4">Carregando módulos...</div>
              ) : modulosKitsSalvos.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Nenhum módulo encontrado</h4>
                  <p className="text-gray-500 mb-4">
                    Esta proposta não possui módulos fotovoltaicos configurados ou os dados não foram encontrados.
                  </p>
                  <button
                    onClick={carregarModulosFotovoltaicos}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {modulosKitsSalvos.map((modulo, index) => (
                    <div key={index} className={`p-4 border rounded-lg ${
                      modulo.encontrado 
                        ? 'border-border bg-muted/30' 
                        : 'border-orange-300 bg-orange-50'
                    }`}>
                      {modulo.encontrado ? (
                        <>
                          <div className="font-medium">{modulo.marca}</div>
                          <div className="text-sm text-muted-foreground">{modulo.modelo}</div>
                          <div className="text-sm">{modulo.potencia}W - {modulo.tecnologia}</div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="h-4 w-4 text-orange-600" />
                            <span className="font-medium text-orange-800">Módulo não encontrado</span>
                          </div>
                          <div className="text-sm text-orange-700 mb-2">
                            O módulo "{modulo.descricaoOriginal}" não foi salvo na página módulos fotovoltaicos.
                          </div>
                          <div className="text-xs text-orange-600">
                            Marca: {modulo.marca} | Potência: {modulo.potencia}W | Tecnologia: {modulo.tecnologia}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Parâmetros da Projeção */}
              <div className="space-y-4">
                <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold`}>Parâmetros da Projeção</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 w-full">
                <div className="w-full min-w-0">
                  <Label htmlFor="periodoAnalisado">Período Analisado (anos)</Label>
                  <Input
                    id="periodoAnalisado"
                    type="number"
                    step="1"
                    min="1"
                    max="50"
                    value={parametrosProjecao.periodoAnalisado}
                    onChange={(e) => setParametrosProjecao(prev => ({
                      ...prev,
                      periodoAnalisado: parseInt(e.target.value) || 30
                    }))}
                    placeholder="30"
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ex: 30 = 30 anos
                  </p>
                </div>
                <div className="w-full min-w-0">
                  <Label htmlFor="inflacaoEnergetica">Inflação Energética Anual (%)</Label>
                  <Input
                    id="inflacaoEnergetica"
                    type="number"
                    step="0.1"
                    min="0"
                    max="20"
                    value={parametrosProjecao.inflacaoEnergeticaAnual}
                    onChange={(e) => setParametrosProjecao(prev => ({
                      ...prev,
                      inflacaoEnergeticaAnual: parseFloat(e.target.value) || 7.0
                    }))}
                    placeholder="7.0"
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ex: 7.0 = 7.0%
                  </p>
                </div>
                <div className="w-full min-w-0">
                  <Label htmlFor="taxaDecaimento">Taxa de Decaimento dos Módulos (%)</Label>
                  <Select
                    value={parametrosProjecao.taxaDecaimentoModulos.toString()}
                    onValueChange={(value) => setParametrosProjecao(prev => ({
                      ...prev,
                      taxaDecaimentoModulos: parseFloat(value)
                    }))}
                  >
                    <SelectTrigger id="taxaDecaimento" className="w-full">
                      <SelectValue placeholder="Selecione a taxa de decaimento..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.42">
                        <div className="flex flex-col items-start">
                          <span className="font-medium">0,42%</span>
                          <span className="text-xs text-muted-foreground">Módulos bifaciais</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="0.45">
                        <div className="flex flex-col items-start">
                          <span className="font-medium">0,45%</span>
                          <span className="text-xs text-muted-foreground">Módulos monofaciais</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selecione o tipo de módulo para definir a taxa de decaimento
                  </p>
                </div>
              </div>

              <Button 
                onClick={calcularProjecao}
                className="w-full"
                disabled={loadingProjecao}
              >
                <Calculator className="h-4 w-4 mr-2" />
                {loadingProjecao ? 'Calculando...' : 'Calcular Projeção'}
              </Button>
            </div>

            {/* Resultados da Projeção */}
            {projecaoCalculada && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Resultados da Projeção</h3>
                
                {/* Cenário SEM Energia Solar */}
                <div className="space-y-4">
                  <h4 className={`${isMobile ? 'text-sm' : 'text-md'} font-medium text-red-600`}>Situação SEM Energia Solar</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                      <div className="text-sm text-red-600 font-medium">Consumo Mensal Médio</div>
                      <div className="text-lg font-bold text-red-700">
                        {formatBrazilianInteger(projecaoCalculada.cenarioSemEnergia.consumoMensalMedio)} kWh/mês
                      </div>
                    </div>
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                      <div className="text-sm text-red-600 font-medium">Consumo Total em {parametrosProjecao.periodoAnalisado} Anos</div>
                      <div className="text-lg font-bold text-red-700">
                        {formatBrazilianInteger(projecaoCalculada.cenarioSemEnergia.consumoTotalPeriodo)} kWh
                      </div>
                    </div>
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                      <div className="text-sm text-red-600 font-medium">Despesa com Fatura em {parametrosProjecao.periodoAnalisado} Anos</div>
                      <div className="text-lg font-bold text-red-700">
                        {formatBrazilianCurrency(projecaoCalculada.cenarioSemEnergia.gastoEnergiaPeriodo)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cenário COM Energia Solar */}
                <div className="space-y-4">
                  <h4 className={`${isMobile ? 'text-sm' : 'text-md'} font-medium text-green-600`}>Situação COM Energia Solar</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                      <div className="text-sm text-green-600 font-medium">Geração Mensal (Ano 1)</div>
                      <div className="text-lg font-bold text-green-700">
                        {formatBrazilianInteger(projecaoCalculada.cenarioComEnergia.geracaoMensalAno1)} kWh/mês
                      </div>
                    </div>
                    <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                      <div className="text-sm text-green-600 font-medium">Geração Total Estimada</div>
                      <div className="text-lg font-bold text-green-700">
                        {formatBrazilianInteger(projecaoCalculada.cenarioComEnergia.geracaoTotalEstimada)} kWh
                      </div>
                    </div>
                    <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                      <div className="text-sm text-green-600 font-medium">Despesa com Fatura em {parametrosProjecao.periodoAnalisado} Anos</div>
                      <div className="text-lg font-bold text-green-700">
                        {formatBrazilianCurrency(projecaoCalculada.cenarioComEnergia.despesaFaturaPeriodoComSolar)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Indicadores Financeiros */}
                <div className={`${isMobile ? 'p-4' : 'p-6'} border border-blue-300 rounded-lg bg-blue-50`}>
                  <h4 className={`${isMobile ? 'text-base' : 'text-lg'} font-bold text-blue-800 mb-2`}>Indicadores Financeiros</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-700`}>Payback Simples</div>
                      <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-blue-800`}>
                        {formatBrazilianNumber(projecaoCalculada.paybackSimples, 1)} anos
                      </div>
                    </div>
                    <div>
                      <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-700`}>Payback Descontado</div>
                      <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-blue-800`}>
                        {formatBrazilianNumber(projecaoCalculada.paybackDescontado, 1)} anos
                      </div>
                    </div>
                    <div>
                      <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-blue-700`}>TIR (Taxa Interna de Retorno)</div>
                      <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-blue-800`}>
                        {formatBrazilianPercentage(projecaoCalculada.roeAnualizado, 1)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botões de Ação da Projeção */}
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} justify-center gap-4 pt-4`}>
                  <Button
                    onClick={salvarProjecao}
                    disabled={salvandoProjecao || projecaoFoiSalva}
                    className={`bg-blue-600 hover:bg-blue-700 text-white ${isMobile ? 'w-full px-4 py-2' : 'px-8 py-3'} rounded-lg font-medium flex items-center justify-center gap-2`}
                    title={projecaoFoiSalva ? "Projeção já foi salva. Calcule uma nova projeção para poder salvar novamente." : ""}
                  >
                    {salvandoProjecao ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        Salvar Projeção
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={deletarProjecao}
                    variant="destructive"
                    className={`${isMobile ? 'w-full px-4 py-2' : 'px-8 py-3'} rounded-lg font-medium flex items-center justify-center gap-2`}
                    disabled={!selectedProposta?.lead_id}
                    title="Apagar apenas a projeção de custos e economia desta proposta, mantendo todos os outros dados intactos"
                  >
                    <Trash2 className="h-5 w-5" />
                    Deletar Projeção
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Seção Final - Geração da Proposta */}
      {selectedProposta && podeCriarProposta(selectedProposta) && (
        <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 w-full overflow-x-hidden">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2 text-xl text-green-800">
              <FileText className="h-6 w-6" />
              Geração da Proposta Final
            </CardTitle>
            <CardDescription className="text-green-700">
              Cliente: {PropostasService.getNomeCliente(selectedProposta.lead)}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Status de Validação */}
            {validacaoProposta && (
              <div className={`p-4 border rounded-lg ${
                validacaoProposta.podeGerar
                  ? 'border-green-300 bg-green-100'
                  : 'border-orange-300 bg-orange-100'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {validacaoProposta.podeGerar ? (
                    <>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-green-800">
                        ✅ Todos os dados necessários estão disponíveis
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                      <span className="text-sm font-medium text-orange-800">
                        ⚠️ Dados insuficientes para gerar proposta
                      </span>
                    </>
                  )}
                </div>

                {!validacaoProposta.podeGerar && validacaoProposta.dadosFaltantes.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-orange-700 mb-2">
                      <strong>Informações necessárias:</strong>
                    </p>
                    <ul className="text-sm text-orange-700 space-y-1">
                      {validacaoProposta.dadosFaltantes.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-orange-500 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 p-2 bg-orange-200 rounded border border-orange-400">
                      <p className="text-xs text-orange-900 font-medium">
                        Complete essas informações nas etapas anteriores antes de gerar a proposta.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Botões de Ação */}
            <div className="flex flex-col items-center gap-4 pt-4">
              {/* Layout em Grid para os Botões Principais */}
              <div className="space-y-4 w-full max-w-2xl">
                {/* Botão Gerar Nova Proposta (largura total quando já existe uma) */}
                {selectedProposta?.proposta_pdf_url && (
                  <Button
                    onClick={gerarProposta}
                    disabled={gerandoProposta || validandoDados || !validacaoProposta?.podeGerar}
                    variant="outline"
                    className={`w-full ${isMobile ? 'px-4 py-3 text-sm' : 'px-6 py-4 text-base'} rounded-lg font-medium flex items-center justify-center gap-3 border-2 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                      validacaoProposta?.podeGerar
                        ? 'border-green-300 text-green-700 hover:bg-green-50'
                        : 'border-gray-300 text-gray-400 cursor-not-allowed'
                    }`}
                    title={!validacaoProposta?.podeGerar ? 'Complete todos os dados necessários antes de gerar a proposta' : ''}
                  >
                    {gerandoProposta ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        Gerando...
                      </>
                    ) : validandoDados ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                        Validando...
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4" />
                        Gerar Nova
                      </>
                    )}
                  </Button>
                )}

                {/* Botão Gerar Proposta Final (largura total quando não existe) */}
                {!selectedProposta?.proposta_pdf_url && (
                  <Button
                    onClick={gerarProposta}
                    disabled={gerandoProposta || validandoDados || !validacaoProposta?.podeGerar}
                    className={`w-full ${isMobile ? 'px-4 py-3 text-sm' : 'px-6 py-4 text-base'} rounded-lg font-semibold flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                      validacaoProposta?.podeGerar
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-gray-400 cursor-not-allowed text-gray-200'
                    }`}
                    title={!validacaoProposta?.podeGerar ? 'Complete todos os dados necessários antes de gerar a proposta' : ''}
                  >
                    {gerandoProposta ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Gerando...
                      </>
                    ) : validandoDados ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
                        Validando...
                      </>
                    ) : (
                      <>
                        <FileText className="h-5 w-5" />
                        Gerar Proposta
                      </>
                    )}
                  </Button>
                )}

                {/* Botões Deletar PDF e Visualizar Proposta lado a lado */}
                {selectedProposta?.proposta_pdf_url && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Botão Deletar PDF da Proposta */}
                    <Button
                      onClick={deletarProposta}
                      disabled={deletandoProposta || !selectedProposta?.proposta_pdf_url}
                      variant="destructive"
                      className={`${isMobile ? 'px-4 py-3 text-sm' : 'px-6 py-4 text-base'} rounded-lg font-semibold flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105`}
                      title={!selectedProposta?.proposta_pdf_url ? 'Esta proposta não possui PDF para deletar' : 'Remove apenas o PDF, mantendo todos os dados da proposta'}
                    >
                      {deletandoProposta ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Removendo PDF...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-5 w-5" />
                          Deletar PDF
                        </>
                      )}
                    </Button>

                    {/* Botão Visualizar Proposta Existente */}
                    <Button
                      onClick={() => {
                        const pdfUrl = selectedProposta.proposta_pdf_url!;
                        try {
                          console.log('🔍 Tentando visualizar proposta:', pdfUrl.substring(0, 100) + '...');
                          
                          // Abrir PDF em nova aba
                          console.log('🌐 Abrindo PDF:', pdfUrl);
                          
                          const newWindow = window.open(pdfUrl, '_blank');
                          
                          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                            console.error('❌ Popup bloqueado ou falha ao abrir');
                            alert('A visualização foi bloqueada. Por favor, permita pop-ups para este site.');
                          } else {
                            console.log('✅ PDF aberto com sucesso');
                          }
                          
                        } catch (error) {
                          console.error('❌ Erro ao visualizar proposta:', error);
                          alert(`Erro ao visualizar proposta: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
                        }
                      }}
                      className="px-6 py-4 text-base rounded-lg font-semibold flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Eye className="h-5 w-5" />
                      Visualizar Proposta
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Seleção de Template */}
            <div className="space-y-4 pt-4 border-t border-green-200">
              <div className="text-center">
                <h4 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-green-800 mb-3`}>Escolha o Template da Proposta</h4>
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} justify-center ${isMobile ? 'gap-2' : 'gap-4'} mb-4`}>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="template-moderno"
                      name="template"
                      checked={!usarTemplateWord}
                      onChange={() => setUsarTemplateWord(false)}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500"
                    />
                    <label htmlFor="template-moderno" className="text-sm font-medium text-gray-900">
                      Template Moderno
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="template-word"
                      name="template"
                      checked={usarTemplateWord}
                      onChange={() => setUsarTemplateWord(true)}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500"
                    />
                    <label htmlFor="template-word" className="text-sm font-medium text-gray-900">
                      Template Word (Proposta_Modelo_BT.docx)
                    </label>
                  </div>
                </div>

                {/* Descrição dos Templates */}
                <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4 mb-6`}>
                  <div className={`p-4 rounded-lg border-2 ${!usarTemplateWord ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <h5 className="font-semibold text-green-800 mb-2">Template Moderno</h5>
                    <ul className="text-xs text-green-700 space-y-1">
                      <li>• Design responsivo e moderno</li>
                      <li>• Elementos gráficos interativos</li>
                      <li>• Layout otimizado para web</li>
                      <li>• Cores dinâmicas e gradientes</li>
                    </ul>
                  </div>
                  <div className={`p-4 rounded-lg border-2 ${usarTemplateWord ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                    <h5 className="font-semibold text-blue-800 mb-2">Template Word</h5>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Baseado no Proposta_Modelo_BT.docx</li>
                      <li>• Layout clássico e profissional</li>
                      <li>• Formatação tradicional</li>
                      <li>• Compatível com padrões Word</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Mensagem Final */}
              <div className="text-center pt-4 border-t border-green-200">
                <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-green-700 px-2`}>
                  Esta proposta será gerada em PDF com todas as informações técnicas e econômicas do sistema fotovoltaico.
                </p>
                <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-600 mt-2 px-2`}>
                  Template selecionado: <strong>{usarTemplateWord ? 'Word (Proposta_Modelo_BT.docx)' : 'Moderno'}</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
